import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { ChatConversation, ChatMessage, chatHistoryService } from '../services/chatHistoryService';
import { Message } from '../types/chat';

interface ConversationState {
  // Conversations
  conversations: ChatConversation[];
  currentConversationId: string | null;
  conversationsLoading: boolean;
  conversationsError: string | null;

  // Messages de la conversation courante
  currentMessages: ChatMessage[];
  messagesLoading: boolean;
  messagesError: string | null;

  // UI State
  sidebarExpanded: boolean;

  // Actions - Conversations
  loadConversations: () => Promise<void>;
  createConversation: (title: string, modelId: string, modelName: string) => Promise<string>;
  selectConversation: (conversationId: string) => Promise<void>;
  updateConversationTitle: (conversationId: string, title: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;

  // Actions - Messages
  loadMessages: (conversationId: string) => Promise<void>;
  addMessage: (conversationId: string, role: 'user' | 'assistant', content: string, metadata?: Record<string, any>) => Promise<void>;

  // Actions - UI
  toggleSidebar: () => void;
  setSidebarExpanded: (expanded: boolean) => void;

  // Actions - Utils
  clearError: () => void;
  reset: () => void;

  // Méthode pour convertir ChatMessage vers Message (pour compatibilité)
  convertToMessages: () => Message[];
}

export const useConversationStore = create<ConversationState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // État initial
        conversations: [],
        currentConversationId: null,
        conversationsLoading: false,
        conversationsError: null,

        currentMessages: [],
        messagesLoading: false,
        messagesError: null,

        sidebarExpanded: true,

        // Actions - Conversations
        loadConversations: async () => {
          set({ conversationsLoading: true, conversationsError: null });

          try {
            const response = await chatHistoryService.getConversations({
              limit: 50,
              offset: 0
            });

            const state = get();
            const hasConversations = response.conversations.length > 0;

            // Auto-sélectionner la première conversation si :
            // - Il n'y a pas de conversation courante
            // - Ou si la conversation courante n'existe plus dans la liste
            let newCurrentConversationId = state.currentConversationId;

            if (hasConversations) {
              const currentExists = response.conversations.some(conv => conv.id === state.currentConversationId);

              if (!state.currentConversationId || !currentExists) {
                newCurrentConversationId = response.conversations[0].id;
              }
            } else {
              newCurrentConversationId = null;
            }

            set({
              conversations: response.conversations,
              currentConversationId: newCurrentConversationId,
              conversationsLoading: false
            });

            // Charger les messages de la conversation sélectionnée
            if (newCurrentConversationId && newCurrentConversationId !== state.currentConversationId) {
              await get().loadMessages(newCurrentConversationId);
            } else if (newCurrentConversationId && state.currentMessages.length === 0) {
              // Si c'est la même conversation mais pas de messages chargés, les charger
              await get().loadMessages(newCurrentConversationId);
            }
          } catch (error) {
            console.error('Failed to load conversations:', error);
            set({
              conversationsError: error instanceof Error ? error.message : 'Unknown error',
              conversationsLoading: false
            });
          }
        },

        createConversation: async (title: string, modelId: string, modelName: string) => {
          try {
            const conversation = await chatHistoryService.createConversation({
              title,
              model_id: modelId,
              model_name: modelName
            });

            // Ajouter la nouvelle conversation en tête de liste
            set(state => ({
              conversations: [conversation, ...state.conversations],
              currentConversationId: conversation.id,
              currentMessages: []
            }));

            return conversation.id;
          } catch (error) {
            console.error('Failed to create conversation:', error);
            set({
              conversationsError: error instanceof Error ? error.message : 'Failed to create conversation'
            });
            throw error;
          }
        },

        selectConversation: async (conversationId: string) => {
          const state = get();

          // Si c'est déjà la conversation courante, ne rien faire
          if (state.currentConversationId === conversationId) {
            return;
          }

          set({
            currentConversationId: conversationId,
            currentMessages: [],
            messagesError: null
          });

          // Charger les messages de cette conversation
          await get().loadMessages(conversationId);
        },

        updateConversationTitle: async (conversationId: string, title: string) => {
          try {
            const updatedConversation = await chatHistoryService.updateConversation(conversationId, { title });

            set(state => ({
              conversations: state.conversations.map(conv =>
                conv.id === conversationId ? updatedConversation : conv
              )
            }));
          } catch (error) {
            console.error('Failed to update conversation:', error);
            set({
              conversationsError: error instanceof Error ? error.message : 'Failed to update conversation'
            });
            throw error;
          }
        },

        deleteConversation: async (conversationId: string) => {
          try {
            await chatHistoryService.deleteConversation(conversationId);

            set(state => {
              const newConversations = state.conversations.filter(conv => conv.id !== conversationId);

              return {
                conversations: newConversations,
                // Si on supprime la conversation courante, sélectionner la première disponible
                currentConversationId: state.currentConversationId === conversationId
                  ? (newConversations[0]?.id || null)
                  : state.currentConversationId,
                // Vider les messages si on supprime la conversation courante
                currentMessages: state.currentConversationId === conversationId ? [] : state.currentMessages
              };
            });

            // Si une nouvelle conversation est sélectionnée, charger ses messages
            const newState = get();
            if (newState.currentConversationId && newState.currentConversationId !== conversationId) {
              await get().loadMessages(newState.currentConversationId);
            }
          } catch (error) {
            console.error('Failed to delete conversation:', error);
            set({
              conversationsError: error instanceof Error ? error.message : 'Failed to delete conversation'
            });
            throw error;
          }
        },

        // Actions - Messages
        loadMessages: async (conversationId: string) => {
          set({ messagesLoading: true, messagesError: null });

          try {
            const response = await chatHistoryService.getMessages(conversationId, {
              limit: 100,
              offset: 0
            });

            set({
              currentMessages: response.messages,
              messagesLoading: false
            });
          } catch (error) {
            console.error('Failed to load messages:', error);
            set({
              messagesError: error instanceof Error ? error.message : 'Failed to load messages',
              messagesLoading: false
            });
          }
        },

        addMessage: async (conversationId: string, role: 'user' | 'assistant', content: string, metadata?: Record<string, any>) => {
          try {
            const message = await chatHistoryService.addMessage(conversationId, {
              role,
              content,
              metadata
            });

            // Ajouter le message à la liste courante si c'est la conversation active
            set(state => {
              if (state.currentConversationId === conversationId) {
                return {
                  currentMessages: [...state.currentMessages, message]
                };
              }
              return state;
            });

            // Mettre à jour le timestamp de la conversation dans la liste
            set(state => ({
              conversations: state.conversations.map(conv =>
                conv.id === conversationId
                  ? { ...conv, updated_at: message.created_at }
                  : conv
              )
            }));
          } catch (error) {
            console.error('Failed to add message:', error);
            set({
              messagesError: error instanceof Error ? error.message : 'Failed to add message'
            });
            throw error;
          }
        },

        // Actions - UI
        toggleSidebar: () => {
          set(state => ({
            sidebarExpanded: !state.sidebarExpanded
          }));
        },

        setSidebarExpanded: (expanded: boolean) => {
          set({ sidebarExpanded: expanded });
        },

        // Actions - Utils
        clearError: () => {
          set({
            conversationsError: null,
            messagesError: null
          });
        },

        reset: () => {
          set({
            conversations: [],
            currentConversationId: null,
            currentMessages: [],
            conversationsLoading: false,
            messagesLoading: false,
            conversationsError: null,
            messagesError: null
          });
        },

        // Conversion pour compatibilité avec l'interface existante
        convertToMessages: () => {
          const { currentMessages } = get();
          return currentMessages.map(msg => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at)
          }));
        }
      }),
      {
        name: 'conversation-store',
        // Ne pas persister les états de loading et error
        partialize: (state) => ({
          sidebarExpanded: state.sidebarExpanded,
          currentConversationId: state.currentConversationId
        }),
      }
    )
  )
);

// Hooks utilitaires
export const useCurrentConversation = () => {
  return useConversationStore(state => {
    const current = state.conversations.find(conv => conv.id === state.currentConversationId);
    return current || null;
  });
};

export const useCurrentMessages = () => {
  return useConversationStore(state => state.currentMessages);
};

// Hook simplifié pour éviter les re-créations
export const useConversationActions = () => {
  const store = useConversationStore();
  return {
    loadConversations: store.loadConversations,
    createConversation: store.createConversation,
    selectConversation: store.selectConversation,
    updateConversationTitle: store.updateConversationTitle,
    deleteConversation: store.deleteConversation,
    addMessage: store.addMessage,
    clearError: store.clearError,
  };
};